/**
 * FiPr.ai inline-SVG wordmark.
 *
 * Spec:
 *   - "Fi" → amber (var --cb-accent ≈ #F5A623)
 *   - "Pr.ai" → white
 *   - Dot over the i in "Fi" → WHITE
 *   - Period in "Pr." → AMBER
 *   - Dot over the i in ".ai" → AMBER
 *   - Background transparent. No tagline.
 *
 * Implementation:
 *   Each glyph is its own `<text>` element with explicit x positioning so the
 *   dots can be precisely overlaid as `<circle>` elements. Lowercase i is
 *   rendered as the dotless variant (U+0131 "ı") so the colored dots above
 *   are the only dots in the glyph.
 */
import React from 'react';

const AMBER = '#F5A623';
const WHITE = '#FFFFFF';

const SIZE_HEIGHTS = { sm: 24, md: 32, lg: 48, xl: 64 };

// Internal viewBox dimensions — chosen so positions stay font-stable across
// browsers using Inter / system-ui at weight 800, letter-spacing -0.02em.
// Tuned by visual inspection at high zoom.
const VB_W = 168;
const VB_H = 60;

export function FiPrIcon({ size = 32, className = '' }) {
  // Retained for callers that still import FiPrIcon (legacy avatar uses).
  // Renders the wordmark — there is no separate icon mark anymore per spec.
  return <FiPrWordmark size={size > 36 ? 'lg' : 'md'} className={className} />;
}

export function FiPrWordmark({
  size = 'md',
  className = '',
  // Legacy props from the previous implementation — accepted but ignored so
  // existing call-sites (`iconSize={…}`, `textSize="3xl"`) don't break.
  iconSize,
  textSize,
}) {
  // Resolve height: explicit `size` token wins, else map legacy textSize.
  const legacyTextMap = { sm: 'sm', md: 'md', lg: 'lg', xl: 'md', '2xl': 'lg', '3xl': 'xl', '4xl': 'xl' };
  const token = SIZE_HEIGHTS[size] ? size : (legacyTextMap[textSize] || 'md');
  const height = SIZE_HEIGHTS[token];
  const width = Math.round((VB_W / VB_H) * height);

  const fontProps = {
    fontFamily: 'Inter, system-ui, -apple-system, "Segoe UI", sans-serif',
    fontWeight: 800,
    fontSize: 56,
    letterSpacing: '-1.2',
  };

  // Per-glyph x positions (baseline y=48 inside the 60-unit-tall viewBox).
  // Empirically tuned for Inter 800.
  const Y = 48;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="FiPr.ai"
      data-testid="fipr-wordmark"
      className={className}
      style={{ display: 'inline-block', verticalAlign: 'middle' }}
    >
      {/* F — amber */}
      <text x="0"   y={Y} fill={AMBER} {...fontProps}>F</text>
      {/* dotless ı — amber (stem of the "i" in "Fi") */}
      <text x="30"  y={Y} fill={AMBER} {...fontProps}>ı</text>
      {/* white dot over the "Fi" i */}
      <circle cx="35.5" cy="11" r="4.6" fill={WHITE} />

      {/* P — white */}
      <text x="42"  y={Y} fill={WHITE} {...fontProps}>P</text>
      {/* r — white */}
      <text x="72"  y={Y} fill={WHITE} {...fontProps}>r</text>
      {/* period in "Pr." — amber */}
      <text x="89"  y={Y} fill={AMBER} {...fontProps}>.</text>

      {/* a — white */}
      <text x="102" y={Y} fill={WHITE} {...fontProps}>a</text>
      {/* dotless ı — white (stem of the "i" in ".ai") */}
      <text x="130" y={Y} fill={WHITE} {...fontProps}>ı</text>
      {/* amber dot over ".ai" i */}
      <circle cx="135.5" cy="11" r="4.6" fill={AMBER} />
    </svg>
  );
}

export default FiPrIcon;
